import { useState, useEffect, useCallback } from 'react';
import { apiGet, ApiResponse, ApiError } from '../../services/api.service';
import { ExtraEventWithDetails } from '../../types/extraEvent';

interface UseGetExtraEventsReturn {
  events: ExtraEventWithDetails[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch extra events for a specific extra ID
 * Only returns events with status 'booked' (backend filters this)
 *
 * @param extraId - The ID of the extra to fetch events for
 * @param eventType - Optional: Filter by event type ('internal' or 'external')
 * @returns Object containing events data, loading state, error state, and refetch function
 */
export function useGetExtraEvents(
  extraId: number | string,
  eventType?: 'internal' | 'external'
): UseGetExtraEventsReturn {
  const [events, setEvents] = useState<ExtraEventWithDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExtraEvents = useCallback(async () => {
    if (!extraId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build URL with optional event_type query parameter
      let url = `/extra_events/${extraId}`;
      if (eventType) {
        url += `?event_type=${eventType}`;
      }

      const response: ApiResponse<ExtraEventWithDetails[]> = await apiGet<
        ExtraEventWithDetails[]
      >(url);

      if (response.success && response.data) {
        setEvents(response.data);
      } else {
        setError(response.error || 'Failed to fetch events');
        setEvents([]);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred while fetching events');
      }
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [extraId, eventType]);

  useEffect(() => {
    fetchExtraEvents();
  }, [fetchExtraEvents]);

  return {
    events,
    loading,
    error,
    refetch: fetchExtraEvents,
  };
}
